import React, { useRef, useState, useEffect, useCallback } from 'react';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const SignaturePad = ({
	onSignatureChange,
	width = 400,
	height = 150,
	penColor = '#000000',
	backgroundColor = '#ffffff',
	disabled = false,
	initialSignature = null,
	className = '',
}) => {
	const canvasRef = useRef(null);
	const [isDrawing, setIsDrawing] = useState(false);
	const [hasSignature, setHasSignature] = useState(false);
	const [ctx, setCtx] = useState(null);

	// Initialize canvas
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const context = canvas.getContext('2d');
		context.strokeStyle = penColor;
		context.lineWidth = 2;
		context.lineCap = 'round';
		context.lineJoin = 'round';
		setCtx(context);

		// Fill with background color
		context.fillStyle = backgroundColor;
		context.fillRect(0, 0, width, height);

		// Load initial signature if provided
		if (initialSignature) {
			const img = new Image();
			img.onload = () => {
				context.drawImage(img, 0, 0);
				setHasSignature(true);
			};
			img.src = initialSignature;
		}
	}, [width, height, penColor, backgroundColor, initialSignature]);

	const getCoordinates = useCallback(
		(e) => {
			const canvas = canvasRef.current;
			const rect = canvas.getBoundingClientRect();
			const scaleX = canvas.width / rect.width;
			const scaleY = canvas.height / rect.height;

			if (e.touches) {
				return {
					x: (e.touches[0].clientX - rect.left) * scaleX,
					y: (e.touches[0].clientY - rect.top) * scaleY,
				};
			}

			return {
				x: (e.clientX - rect.left) * scaleX,
				y: (e.clientY - rect.top) * scaleY,
			};
		},
		[]
	);

	const startDrawing = useCallback(
		(e) => {
			if (disabled) return;

			e.preventDefault();
			const { x, y } = getCoordinates(e);

			ctx.beginPath();
			ctx.moveTo(x, y);
			setIsDrawing(true);
		},
		[ctx, disabled, getCoordinates]
	);

	const draw = useCallback(
		(e) => {
			if (!isDrawing || disabled) return;

			e.preventDefault();
			const { x, y } = getCoordinates(e);

			ctx.lineTo(x, y);
			ctx.stroke();
			setHasSignature(true);
		},
		[ctx, isDrawing, disabled, getCoordinates]
	);

	const stopDrawing = useCallback(() => {
		if (!isDrawing) return;

		ctx.closePath();
		setIsDrawing(false);

		// Notify parent of signature change
		if (hasSignature && onSignatureChange) {
			const canvas = canvasRef.current;
			const signatureData = canvas.toDataURL('image/png');
			onSignatureChange(signatureData);
		}
	}, [ctx, isDrawing, hasSignature, onSignatureChange]);

	const clearSignature = useCallback(() => {
		if (!ctx) return;

		ctx.fillStyle = backgroundColor;
		ctx.fillRect(0, 0, width, height);
		setHasSignature(false);

		if (onSignatureChange) {
			onSignatureChange(null);
		}
	}, [ctx, width, height, backgroundColor, onSignatureChange]);

	// Get signature data
	const getSignatureData = useCallback(() => {
		if (!hasSignature) return null;
		const canvas = canvasRef.current;
		return canvas.toDataURL('image/png');
	}, [hasSignature]);

	// Expose getSignatureData to parent via ref
	useEffect(() => {
		if (canvasRef.current) {
			canvasRef.current.getSignatureData = getSignatureData;
			canvasRef.current.clearSignature = clearSignature;
			canvasRef.current.hasSignature = hasSignature;
		}
	}, [getSignatureData, clearSignature, hasSignature]);

	return (
		<div className={`relative ${className}`}>
			<canvas
				ref={canvasRef}
				width={width}
				height={height}
				className={`border-2 border-dashed border-gray-300 rounded-lg cursor-crosshair touch-none ${
					disabled ? 'opacity-50 cursor-not-allowed' : ''
				}`}
				style={{ width: '100%', height: 'auto', maxWidth: width }}
				onMouseDown={startDrawing}
				onMouseMove={draw}
				onMouseUp={stopDrawing}
				onMouseLeave={stopDrawing}
				onTouchStart={startDrawing}
				onTouchMove={draw}
				onTouchEnd={stopDrawing}
			/>
			{!disabled && (
				<div className="absolute top-2 right-2 flex gap-1">
					{hasSignature && (
						<button
							type="button"
							onClick={clearSignature}
							className="p-1.5 bg-white rounded-full shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors"
							title="Clear signature"
						>
							<ArrowPathIcon className="w-4 h-4 text-gray-500" />
						</button>
					)}
				</div>
			)}
			{!hasSignature && !disabled && (
				<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
					<span className="text-gray-400 text-sm">Sign here</span>
				</div>
			)}
		</div>
	);
};

export default SignaturePad;
